import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  SystemOptionCondenserType,
  SystemOptionType,
  ValidSystemType
} from 'data/types';
import { useUpsertCustomSystemMutation } from 'features/api/project-api';
import {
  beginCustomSystemSave,
  bumpCustomSystemsRevision,
  endCustomSystemSave
} from 'features/systems';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildSystemsForSwappedOption,
  resolveCustomSystemName
} from 'utils/custom-system-swap';
import { describeSwapChanges, resolveSwap, SwapLocks } from 'utils/system-swap';
import {
  buildCondenserViewFromFilteredSystem,
  buildCustomSystemEntries
} from 'utils/systems';
import { notification } from 'utils/ui';

export type ComponentSwapHandler = (
  type: 'condenser' | 'airHandler',
  productId: number,
  zoneId: string | undefined,
  locks: SwapLocks
) => boolean;

type UseEditableSystemMapResult = {
  systemOptionIndex: number | undefined;
  filteredIndexMap: Record<string, number | null>;
  getDisplayCondenser: (
    condenser: SystemOptionCondenserType
  ) => SystemOptionCondenserType;
  makeComponentSwapHandler: (condenserUniqueId: string) => ComponentSwapHandler;
};

export function useEditableSystemMap(
  option: SystemOptionType,
  onCustomSystemSaved?: (uniqueId: string) => void
): UseEditableSystemMapResult {
  const dispatch = useAppDispatch();
  const systemOptions = useAppSelector((state) => state.systems.systemOptions);
  const systemCombinationsFull = useAppSelector(
    (state) => state.systems.systemCombinationsFull
  );
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const condenserProducts = useAppSelector(
    (state) => state.data.condenserProducts
  );
  const airHandlerProducts = useAppSelector(
    (state) => state.data.airHandlerProducts
  );
  const [upsertCustomSystem] = useUpsertCustomSystemMutation();
  const [filteredIndexMap, setFilteredIndexMap] = useState<
    Record<string, number | null>
  >({});
  const [overrides, setOverrides] = useState<Record<string, ValidSystemType>>(
    {}
  );
  const createdCustomSystemRef = useRef<{
    session: number;
    uniqueId: string;
    name: string;
  } | null>(null);
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());
  const editSessionRef = useRef(0);

  useEffect(() => {
    editSessionRef.current += 1;
    setOverrides({});
  }, [option.id]);

  const systemOptionIndex = useMemo(() => {
    const configId = option.configuration.uniqueId;
    const options = systemOptions[configId] ?? [];
    const idx = options.findIndex((o) => o.uniqueId === option.uniqueId);
    return idx === -1 ? undefined : idx;
  }, [systemOptions, option]);

  const fullConfig = useMemo(
    () =>
      configurations.find(
        (c) => c.uniqueId === option.configuration.uniqueId
      ) ?? null,
    [configurations, option]
  );

  useEffect(() => {
    if (!fullConfig) return;

    const newMap: Record<string, number | null> = {};
    option.configuration.condensers.forEach((condenser) => {
      const fc = fullConfig.condensers.find(
        (c) => c.uniqueId === condenser.uniqueId
      );
      const systems = fc?.validSystems ?? [];
      if (systems.length === 0) return;

      const matchIdx = systems.findIndex((fs) => {
        if (fs.condenserProductId !== condenser.selectedProduct?.productId)
          return false;
        return condenser.zones.every((zone) => {
          if (!zone.selectedProduct) return true;
          for (let n = 1; n <= 10; n += 1) {
            const uniqueKey = `airHandler${n}Unique` as keyof ValidSystemType;
            const productKey =
              `airHandler${n}ProductId` as keyof ValidSystemType;
            if (fs[uniqueKey] === zone.uniqueId) {
              return fs[productKey] === zone.selectedProduct.productId;
            }
          }
          return false;
        });
      });

      newMap[condenser.uniqueId] = matchIdx === -1 ? null : matchIdx;
    });

    setFilteredIndexMap(newMap);
  }, [option.uniqueId, fullConfig]);

  const persistSwap = useCallback(
    (condenserUniqueId: string, targetSystem: ValidSystemType) => {
      if (!fullConfig) return;

      const configId = fullConfig.uniqueId;
      const systemsForNewOption = buildSystemsForSwappedOption(
        option,
        fullConfig,
        filteredIndexMap,
        overrides,
        condenserUniqueId,
        targetSystem
      );
      const entries = buildCustomSystemEntries(systemsForNewOption, fullConfig);
      const existingCustomNames = (systemCombinationsFull[configId] ?? [])
        .filter((c) => c.tags.includes('Custom'))
        .map((c) => c.name ?? '');
      const resolvedName = resolveCustomSystemName(option, existingCustomNames);
      const isCustom = option.tags.includes('Custom');
      const optionUniqueId = option.uniqueId;
      const session = editSessionRef.current;

      dispatch(beginCustomSystemSave({ configId }));
      persistQueueRef.current = persistQueueRef.current.then(async () => {
        const created = createdCustomSystemRef.current;
        const sessionSystem =
          created && created.session === session ? created : null;
        const uniqueId = isCustom ? optionUniqueId : sessionSystem?.uniqueId;
        const name = isCustom
          ? resolvedName
          : sessionSystem?.name ?? resolvedName;

        try {
          const result = await upsertCustomSystem({
            uniqueId,
            configurationUniqueId: configId,
            name,
            entries
          }).unwrap();
          createdCustomSystemRef.current = {
            session,
            uniqueId: result.uniqueId,
            name
          };
          dispatch(bumpCustomSystemsRevision({ configId }));
          onCustomSystemSaved?.(result.uniqueId);
        } catch (err) {
          console.error(
            'Failed to save custom system from component swap',
            err
          );
        } finally {
          dispatch(endCustomSystemSave({ configId }));
        }
      });
    },
    [
      fullConfig,
      option,
      filteredIndexMap,
      overrides,
      systemCombinationsFull,
      upsertCustomSystem,
      dispatch,
      onCustomSystemSaved
    ]
  );

  const makeComponentSwapHandler = useCallback(
    (condenserUniqueId: string): ComponentSwapHandler =>
      (type, productId, zoneId, locks) => {
        const fc = fullConfig?.condensers.find(
          (c) => c.uniqueId === condenserUniqueId
        );
        const systems = fc?.validSystems ?? [];
        if (systems.length === 0) return false;

        const currentIdx = filteredIndexMap[condenserUniqueId];
        if (currentIdx === undefined) return false;

        const current =
          overrides[condenserUniqueId] ??
          (currentIdx === null ? null : systems[currentIdx]);
        if (!current) return false;

        const resolution = resolveSwap(
          systems,
          current,
          type,
          productId,
          zoneId,
          locks
        );

        if (!resolution) {
          // Returning false falls through to the legacy system-map path,
          // which owns messaging for this surface: it either announces the
          // changes of the custom it saves (notifyDisplayedSystemChanges) or
          // announces unavailability at its dead end.
          return false;
        }

        setOverrides((prev) => ({
          ...prev,
          [condenserUniqueId]: resolution.system
        }));
        persistSwap(condenserUniqueId, resolution.system);
        if (resolution.changes.length > 0) {
          const zoneNames = new Map(
            (fc?.zones ?? []).map((zone) => [zone.uniqueId, zone.name])
          );
          notification(
            `Some components were changed to make this system work:\n${describeSwapChanges(
              resolution.changes,
              {
                condensers: condenserProducts,
                airHandlers: airHandlerProducts
              },
              zoneNames
            )}`,
            'warning'
          );
        }
        return true;
      },
    [
      fullConfig,
      filteredIndexMap,
      overrides,
      persistSwap,
      condenserProducts,
      airHandlerProducts
    ]
  );

  const displayCondenserById = useMemo(() => {
    const byId = new Map<string, SystemOptionCondenserType>();
    if (!fullConfig) return byId;
    option.configuration.condensers.forEach((condenser) => {
      const fc = fullConfig.condensers.find(
        (c) => c.uniqueId === condenser.uniqueId
      );
      if (!fc) return;

      const override = overrides[condenser.uniqueId];
      const currentIndex = filteredIndexMap[condenser.uniqueId] ?? null;
      const system =
        override ??
        (currentIndex === null ? null : fc.validSystems[currentIndex]);
      if (!system) return;

      const built = buildCondenserViewFromFilteredSystem(
        system,
        fc,
        condenserProducts,
        airHandlerProducts
      );
      if (built) byId.set(condenser.uniqueId, built);
    });
    return byId;
  }, [
    option,
    overrides,
    filteredIndexMap,
    fullConfig,
    condenserProducts,
    airHandlerProducts
  ]);

  const getDisplayCondenser = useCallback(
    (condenser: SystemOptionCondenserType): SystemOptionCondenserType =>
      displayCondenserById.get(condenser.uniqueId) ?? condenser,
    [displayCondenserById]
  );

  return {
    systemOptionIndex,
    filteredIndexMap,
    getDisplayCondenser,
    makeComponentSwapHandler
  };
}
